<template>
  <div class="w-100 grid-container">
    <configuration-section-header
      class="my-0"
      :section-icon="'far fa-map-marker-exclamation'"
      :section-title="$tc('configuration.sections.incidentOrigins', 2)"
      @changedPageSize="changedPageSize($event)"
      @changedMode="changedMode($event)"
    >
      <b-button
        v-tooltip="$t('configuration.actions.activate')"
        :disabled="!canActivateBatch"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="activateDeactivateSelection(true, selectedRows)"
      >
        <i
          class="far fa-check-circle"
          aria-hidden="true"
        />
      </b-button>
      <b-button
        v-tooltip="$t('configuration.actions.deactivate')"
        :disabled="!canDeactivateBatch"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="activateDeactivateSelection(false, selectedRows)"
      >
        <i
          class="far fa-times-circle"
          aria-hidden="true"
        />
      </b-button>
      <div
        style="width:1px; height:18px; background: #d0d0d0"
        class="mx-3"
      />
      <b-button
        v-shortkey="['alt', 'del']"
        v-tooltip="$t('configuration.actions.delete')"
        :disabled="!selectedRows.length || checkOriginsSystemDefined"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="deleteMorphology(selectedRows)"
        @shortkey="deleteMorphology(selectedRows)"
      >
        <i
          class="far fa-trash-alt"
          aria-hidden="true"
        />
      </b-button>
      <b-button
        v-shortkey="['alt', 'e']"
        v-tooltip="$t('configuration.actions.update')"
        :disabled="!selectedRows.length || checkOriginsSystemDefined"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="showConfigurationIncidentOriginsForm($t('configuration.actions.update'), selectedRows[0])"
        @shortkey="showConfigurationIncidentOriginsForm($t('configuration.actions.update'), selectedRows[0])"
      >
        <i
          class="far fa-edit"
          aria-hidden="true"
        />
      </b-button>
      <b-button
        v-shortkey="['ctrl', 'enter']"
        v-tooltip="$t('configuration.actions.add')"
        class="btn__icon btn__icon--primary px-2 py-0"
        variant="link"
        @click="showConfigurationIncidentOriginsForm($t('configuration.actions.add'))"
        @shortkey="showConfigurationIncidentOriginsForm($t('configuration.actions.add'))"
      >
        <i
          class="far fa-plus"
          aria-hidden="true"
        />
      </b-button>
    </configuration-section-header>
    <div class="flex-grow-1">
      <vt-grid
        class="configuration-grid"
        :grid-options="gridOptions"
        :column-defs="columnDefs"
        :component-code="gridUiComponent"
      />
    </div>
    <modal-wrapper ref="incidentOriginsModalWrapper">
      <configuration-incident-origins-modal
        :action-title="actionTitle"
        @update="patchOrigins($event)"
        @create="createOrigin($event)"
      />
    </modal-wrapper>
  </div>
</template>

<script>
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader'
import VtGrid from '@/components/VtGrid/VtGrid'
import ConfigurationIncidentOriginsGridColumns from '@/modules/configurationModule/detail/incidents/incidentsOrigins/ConfigurationIncidentOriginsGridColumns'
import ConfigurationIncidentOriginsModal from '@/modules/configurationModule/detail/incidents/incidentsOrigins/ConfigurationIncidentOriginsModal'
import gridSideBarConfig from '@/grid/gridSideBarConfig'
import { PaginatorDatasource } from '@/shared/resources/datasources/paginator/paginator-datasource'
import IncidentOriginsConfigurationServices from '@/api/resources/configuration/incidents/origins/IncidentOriginsConfigurationServices'
import { some } from 'lodash'

export default {
    name: 'IncidentOriginsConfigurationGrid',
    components: {
        ConfigurationSectionHeader,
        VtGrid,
        ConfigurationIncidentOriginsModal
    },
    data() {
        return {
            gridUiComponent: 'uicomponent.configuration.global.incidents.incidentsOrigin',
            actionTitle: null,
            selectedRows: [],
            columnDefs: ConfigurationIncidentOriginsGridColumns(),
            mode: {
                size: 25
            }
        }
    },
    computed: {
        rowHeight() {
            return this.mode?.size
        },
        gridOptions() {
            return {
                sideBar: gridSideBarConfig,
                getContextMenuItems: this.getContextMenuItems,
                onGridReady: this.onGridReady,
                onSelectionChanged: this.onSelectionChanged,
                paginationPageSize: 20,
                paginationAutoPageSize: false,
                getRowHeight: () => this.rowHeight
            }
        },
        hasActiveOrigin() {
            return this.selectedRows.find(origin => origin.active)
        },
        hasDeactiveOrigin() {
            return this.selectedRows.find(origin => !origin.active)
        },
        hasAllActivated() {
            return this.selectedRows.length === (this.selectedRows.filter(origin => origin.active).length)
        },
        hasAllDeactivated() {
            return this.selectedRows.length === (this.selectedRows.filter(origin => !origin.active).length)
        },
        canDeactivateBatch() {
            return this.selectedRows.length && this.hasActiveOrigin && !this.hasAllDeactivated
        },
        canActivateBatch() {
            return this.hasDeactiveOrigin && !this.hasAllActivated
        },
        checkOriginsSystemDefined() {
            // If there is one in the collection that has the field 'systemDefined' defined(true), it will not be allowed to delete or update
            return some(this.selectedRows, 'systemDefined')
        }
    },
    methods: {
        async activateDeactivateSelection(option, selectionBatch) {
            const originsToPatch = selectionBatch.filter(origin => origin.active !== option).map(origin => {
                return {
                    active: option,
                    id: origin.id
                }
            })
            if (originsToPatch.length) {
                this.updateLoading(true)
                const [err] = await this.toCall(IncidentOriginsConfigurationServices.patchOriginsBatch(originsToPatch))
                this.updateLoading(false)
                if (err) {
                    this.showErrorModalGlobalFromErrorResponse(err)
                    return
                }
                if (selectionBatch.length > 1) this.showToastSuccessGlobal(this.$tc('configuration.toastMessages.updated', 2, { elements: selectionBatch.length }))
                else this.showToastSuccessGlobal(this.$tc('configuration.toastMessages.updated', 1))
                this.reloadData()
            }
        },
        async patchOrigins(origin) {
            const originTypeRequest = this.mapOriginToRequest(origin)
            this.updateLoading(true)
            const [err, data] = await this.toCall(IncidentOriginsConfigurationServices.patchOriginsBatch([originTypeRequest]))
            this.updateLoading(false)
            if (err) {
                this.showErrorModalGlobalFromErrorResponse(err)
                return
            } else if (data.length) {
                this.handleErrors(data)
                return
            }
            this.$refs.incidentOriginsModalWrapper.hide()
            this.showToastSuccessGlobal(this.$tc('configuration.toastMessages.updated', 1))
            this.reloadData()
        },
        async createOrigin(origin) {
            const originTypeRequest = this.mapOriginToRequest(origin)
            this.updateLoading(true)
            const [err] = await this.toCall(IncidentOriginsConfigurationServices.createOrigin(originTypeRequest))
            this.updateLoading(false)
            if (err) {
                this.showErrorModalGlobalFromErrorResponse(err)
                return
            }
            this.$refs.incidentOriginsModalWrapper.hide()
            this.showToastSuccessGlobal(this.$t('configuration.toastMessages.created'))
            this.reloadData()
        },
        async deleteOriginsBatch(origins) {
            const originsToDelete = origins.map(origin => origin.id)
            this.updateLoading(true)
            const [err, response] = await this.toCall(IncidentOriginsConfigurationServices.deleteOriginsBatch(originsToDelete))
            this.updateLoading(false)
            if (err) {
                this.showErrorModalGlobalFromErrorResponse(err)
                return
            } else if (response.length) {
                this.handleErrors(response)
                return
            }
            if (originsToDelete.length > 1) this.showToastSuccessGlobal(this.$tc('configuration.toastMessages.deleted', 2, { elements: originsToDelete.length }))
            else this.showToastSuccessGlobal(this.$tc('configuration.toastMessages.deleted', 1))
            this.reloadData()
        },
        reloadData() {
            this.gridApi.deselectAll()
            this.gridApi.setServerSideDatasource(new PaginatorDatasource(IncidentOriginsConfigurationServices))
        },
        getContextMenuItems(params) {
            if (!params?.node) {
                return []
            } else {
                return [
                    {
                        name: this.$t('configuration.actions.update'),
                        cssClasses: this.selectedRows.length > 1 || params?.node.data.systemDefined ? ['not-show'] : '',
                        action: () => this.showConfigurationIncidentOriginsForm(this.$t('configuration.actions.update'), params?.node.data)
                    },
                    {
                        name: this.$t('configuration.actions.delete'),
                        cssClasses: this.selectedRows.length < 1 || this.checkOriginsSystemDefined ? ['not-show'] : '',
                        action: () => this.deleteMorphology(this.selectedRows)
                    },
                    {
                        name: params?.node.data.active ? this.$t('configuration.actions.deactivate') : this.$t('configuration.actions.activate'),
                        cssClasses: this.selectedRows.length ? ['not-show'] : '',
                        action: () => this.activateDeactivateSelection(!params.node?.data.active, [params?.node.data])
                    },
                    {
                        name: this.$t('configuration.actions.activate'),
                        cssClasses: this.canActivateBatch ? '' : ['not-show'],
                        action: () => this.activateDeactivateSelection(true, this.selectedRows)
                    },
                    {
                        name: this.$t('configuration.actions.deactivate'),
                        cssClasses: this.canDeactivateBatch ? '' : ['not-show'],
                        action: () => this.activateDeactivateSelection(false, this.selectedRows)
                    }
                ]
            }
        },
        onSelectionChanged({ api }) {
            this.selectedRows = api.getSelectedRows()
        },
        onGridReady({ api }) {
            this.gridApi = api
            api.setServerSideDatasource(new PaginatorDatasource(IncidentOriginsConfigurationServices))
        },
        showConfigurationIncidentOriginsForm(title, selectedRows) {
            this.actionTitle = title
            if (selectedRows) {
                this.$refs.incidentOriginsModalWrapper.show([selectedRows])
            } else {
                this.$refs.incidentOriginsModalWrapper.show([])
            }
        },
        async deleteMorphology(origins) {
            if (origins.length) {
                const promise = await this.showConfirmationModalGlobal(`${this.$t('configuration.grid.deleteConfirmation')} ${this.selectedRows.length} ${this.$tc('configuration.grid.element', this.selectedRows.length)}, <br/> ${this.$t('configuration.grid.deleteQuestion')}`, this.$t('configuration.actions.delete'))
                if (promise) {
                    await this.deleteOriginsBatch(origins)
                }
            }
        },
        mapOriginToRequest(origin) {
            return {
                active: origin.active,
                id: origin.id,
                descriptionTranslations: origin.descriptionTranslations.map(description => ({ languageId: description.language.id, notes: description.notes, value: description.value })),
                nameTranslations: origin.nameTranslations.map(description => ({ languageId: description.language.id, notes: description.notes, value: description.value })),
                workPhaseId: origin.workPhase.id
            }
        },
        changedPageSize(pageSize) {
            this.gridApi.paginationSetPageSize(pageSize)
        },
        changedMode(selectedOption) {
            this.mode = selectedOption
            this.reloadData()
        }
    }
}
</script>
